import { useState, useEffect, useRef } from "react";
import { PlusIcon } from "@heroicons/react/20/solid";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import useDebounce from "../hooks/useDebounce.js";
import DataTable from "../components/Client/DataTable";
import DataTableDescription from "../components/Client/DataTableDescription";
import PrimaryButton from "../components/Shared/PrimaryButton.js";
import Modal from "../components/Shared/Modal";
import ClientForm from "../components/Client/ClientForm";
import { FORM_MODE } from "../config/constants";
import EmptyState from "../components/Shared/EmptyState";
import DataPagination from "../components/Shared/DataPagination.js";
import SearchBar from "../components/Shared/SearchBar.js";
import { getClients, saveClient } from "../api/clientApi.js";
import usePagination from "../hooks/usePagination.js";
import useCreateIntent from "../hooks/useCreateIntent";
import SelectMenu from "../components/Shared/SelectMenu.js";
import ClientListPageSkeleton from "../components/Shared/LoadingSkeletons/ClientListPageSkeleton.js";
import { useToast } from "../hooks/useToast";

const ClientListPage = () => {
  const [showDrawer, setShowDrawer] = useState(false);
  const [showEmptyState, setShowEmptyState] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const modalCloseRef = useRef(null);
  const [search, setSearch] = useState("");
  const [clientFilter, setClientFilter] = useState("active");
  const [, setSearchParams] = useSearchParams();
  const debouncedSearch = useDebounce(search, 500);
  const isSearchPending = search !== debouncedSearch;
  const { page, limit } = usePagination(1, 10);

  const queryClient = useQueryClient();
  const toast = useToast();

  // Mobile bottom-nav "+" deep-links here with ?new=client
  useCreateIntent("client", () => setShowDrawer(true));

  const filterOptions = [
    { id: "active", name: "Active" },
    { id: "all", name: "All" },
    { id: "archived", name: "Archived" },
  ];

  const { data: clientList, isLoading: isLoadingGetClients } = useQuery({
    queryKey: [
      "clientList",
      { page, limit, search: debouncedSearch, filter: clientFilter },
    ],
    queryFn: ({ queryKey }) => {
      const [, params] = queryKey;
      const { filter, ...otherParams } = params;
      const apiParams = {
        ...otherParams,
        ...(filter === "archived" && { archived: true }),
        ...(filter === "active" && { archived: false }),
      };
      return getClients(apiParams);
    },
    placeholderData: keepPreviousData,
  });

  const { data: totalClientsData, isLoading: isLoadingTotalClients } = useQuery({
    queryKey: ["clientListTotal"],
    queryFn: () => getClients({ page: 1, limit: 1 }),
  });

  const { mutateAsync: mutateSaveClient } = useMutation({
    mutationFn: saveClient,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clientList"] });
      queryClient.invalidateQueries({ queryKey: ["clientListTotal"], exact: true });
      toast.success("Client Added", "New client has been added successfully");
      closeDrawerHandler();
    },
  });

  const closeDrawerHandler = () => {
    setShowDrawer(false);
    setIsFormDirty(false);
  };

  const handleSearch = (value) => {
    setSearch(value);
    setSearchParams({ page: 1, limit });
  };

  const handleFilterChange = (selectedFilter) => {
    setClientFilter(selectedFilter.id);
    setSearchParams({ page: 1, limit });
  };

  // Show the full-page empty state only when there are zero clients across all statuses
  useEffect(() => {
    if (isLoadingGetClients || isLoadingTotalClients) return;
    const hasAnyClients = totalClientsData?.total > 0;
    setShowEmptyState(!hasAnyClients);
  }, [totalClientsData, isLoadingGetClients, isLoadingTotalClients]);

  const showDataTable = clientList?.clients.length > 0;

  // Helper function for empty state text based on current context
  const getEmptyStateText = () => {
    if (debouncedSearch) {
      return {
        title: "No clients found",
        description:
          "Try adjusting your search terms or check if the client is archived.",
        showButton: false,
      };
    }

    if (clientFilter === "archived") {
      return {
        title: "No archived clients",
        description:
          "Clients that have been archived will appear here. Check the 'Active' or 'All' filters to see your current clients.",
        showButton: false,
      };
    }

    // Default case for active/all with no search
    return {
      title: "No clients yet",
      description:
        "Get started by adding your first client to manage appointments and bookings.",
      showButton: true,
    };
  };

  const emptyStateConfig = getEmptyStateText();

  return (
    <>
      {!showEmptyState ? (
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="sm:flex sm:items-center">
            <div className="sm:flex-auto">
              <DataTableDescription>
                A list of all the clients in your account including their name,
                email, phone number, and address.
              </DataTableDescription>
            </div>
            <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
              <PrimaryButton onClick={() => setShowDrawer(true)} className="inline-flex items-center">
                <PlusIcon aria-hidden="true" className="-ml-0.5 mr-1.5 size-5" />
                Add Client
              </PrimaryButton>
            </div>
          </div>
          <div className="mt-6 mb-4 w-full flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-end">
            <div className="w-44 order-2 sm:order-1">
              <SelectMenu
                label="Status"
                data={filterOptions}
                defaultSelected={filterOptions.find(
                  (option) => option.id === clientFilter
                )}
                onSelect={handleFilterChange}
              />
            </div>
            <div className="w-full max-w-md order-1 sm:order-2 sm:-mb-4">
              <SearchBar
                value={search}
                onChange={handleSearch}
                isLoading={isSearchPending || isLoadingGetClients}
                placeholder="Search by name, email, or phone"
              />
            </div>
          </div>

          {isLoadingGetClients ? (
            <ClientListPageSkeleton />
          ) : showDataTable ? (
            <>
              <DataTable
                clients={
                  clientList?.clients.length > 0 ? clientList.clients : []
                }
              />
              <DataPagination
                currentPage={parseInt(page)}
                resultsPerPage={parseInt(limit)}
                totalResults={clientList?.total ? clientList?.total : 0}
              />
            </>
          ) : (
            <EmptyState
              text={emptyStateConfig.title}
              subText={emptyStateConfig.description}
              {...(emptyStateConfig.showButton && {
                buttonText: "Add Client",
                onClick: () => setShowDrawer(true),
              })}
            />
          )}
        </div>
      ) : null}

      {showDrawer ? (
        <Modal
          open={showDrawer}
          onClose={closeDrawerHandler}
          title="Create New Client"
          size="md"
          confirmClose={isFormDirty}
          closeRef={modalCloseRef}
        >
          <ClientForm
            closeDrawerHandler={closeDrawerHandler}
            saveClient={mutateSaveClient}
            mode={FORM_MODE.CREATE}
            onCancelHandler={() => modalCloseRef.current?.()}
            setIsFormDirty={setIsFormDirty}
          />
        </Modal>
      ) : null}

      {showEmptyState ? (
        <EmptyState
          text={emptyStateConfig.title}
          subText={emptyStateConfig.description}
          {...(emptyStateConfig.showButton && {
            buttonText: "New Client",
            onClick: () => setShowDrawer(true),
          })}
        />
      ) : null}
    </>
  );
};

export default ClientListPage;
